import { Button, Label, Modal, Spinner, TextInput } from 'flowbite-react';
import { debounce, get } from 'lodash';
import React, {
	ChangeEvent,
	ClipboardEvent,
	FormEvent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { changeEmailOrPhone, requestCallback, resolveOtpSessionId, verifyOtp } from 'src/apis/user';
import { isRecruiterSide } from 'src/config/env.config';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPhoneVerificationStatus, useGetUniquePhoneNumber } from 'src/hooks/user.hooks';
import { useTimeout } from 'webrix/hooks';
import OtpInput from '../shared/OtpInput';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onCompleteScheduling?: () => void;
	isRequestingForCallback?: boolean;
}

type InputError = {
	message: string;
	type: 'success' | 'failure';
};

const CompletePhoneVerificationModal = ({ isOpen, onClose, onCompleteScheduling, isRequestingForCallback }: Props) => {
	const { user } = useUserContext();
	const toast = useToast();
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otp, setOtp] = useState(['', '', '', '', '', '']);
	const [time, setTime] = useState(30);
	const [isResendEnabled, setIsResendEnabled] = useState(false);
	const [step, setStep] = useState<number>(0);

	const [phone, setPhone] = useState('');
	const [phoneError, setPhoneError] = useState<InputError>();

	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const { start, stop } = useTimeout(() => {
		if (time > 0) {
			setTime(time - 1);
		}
	}, 1000);

	useEffect(() => {
		if (time <= 0) {
			setIsResendEnabled(true);
			stop();
		}
	}, [stop, time]);

	useEffect(() => {
		step === 1 && start();
	}, [step, start]);

	const phoneNumber = useGetUniquePhoneNumber(phone, user?._id || '', data => {
		if (data.success) {
			setPhoneError(undefined);
			setPhoneError({
				type: 'failure',
				message: `This Phone number is already registered.`,
			});
		} else {
			setPhoneError(undefined);
		}
	});

	const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
		const newOtp = [...otp];
		newOtp[index] = event.target.value;
		setOtp(newOtp);
		if (event.target.value && index < otp.length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Backspace' && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasteData = event.clipboardData.getData('text/plain').slice(0, 4);
		const newOtp = [...otp];
		pasteData.split('').forEach((digit, index) => {
			newOtp[index] = digit;
		});
		setOtp(newOtp);
	};

	const requestCallBackFn = useMutation(['requestCallback'], requestCallback, {
		onSuccess() {
			queryClient.refetchQueries(['verifyCookie']);
		},
		onError(err) {
			console.error(err);
			queryClient.refetchQueries(['verifyCookie']);
		},
	});

	const generateVerificationCodeFn = useMutation({
		mutationKey: 'changeEmailOrPhone',
		mutationFn: changeEmailOrPhone,
		onSuccess() {
			toast({
				type: 'success',
				title: 'An OTP has sent to your registered phone number.',
			});
			phoneVerificationStatus.refetch();
			setPhoneError(undefined);
			setIsLoading(false);
		},
		onError(err: Error) {
			const errorMessage = get(err, ['response', 'data', 'message'], err.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setPhoneError(undefined);
			setIsLoading(false);
		},
	});

	const verifyPhoneFn = useMutation({
		mutationKey: 'verifyOtp',
		mutationFn: verifyOtp,
		onSuccess() {
			// clear userId info into localstorage
			localStorage.removeItem('userId');
			toast({
				type: 'success',
				title: 'Phone verified successfully',
			});
			phoneVerificationStatus.refetch();
			setIsLoading(false);
			queryClient.refetchQueries(['verifyCookie']);
			if (isRequestingForCallback) {
				requestCallBackFn.mutate(user?._id || '');
			} else {
				onCompleteScheduling && onCompleteScheduling();
			}
			onClose();
		},
		onError(err: Error) {
			toast({
				type: 'error',
				title: err.message,
			});
			setIsLoading(false);
			onClose();
		},
	});

	const phoneVerificationStatus = useGetPhoneVerificationStatus(phone);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setIsLoading(true);

		const otpSessionId = await resolveOtpSessionId(phone, phoneVerificationStatus.data?.verification?.otpSessionId);
		verifyPhoneFn.mutate({
			emailOrPhone: phone,
			otp: otp.join(''),
			otpSessionId,
		});
	};

	const hanldeEnterAnotherNumber = () => {
		setPhone('');
		setOtp(['', '', '', '', '', '']);
		setStep(0);
	};

	useEffect(() => {
		if (user?.phone) setPhone(user?.phone);
		setPhoneError(undefined);
		setOtp(['', '', '', '', '', '']);
		setStep(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const changeHandler = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
		setPhone(event.target.value);
	};

	useEffect(() => {
		if (phone.length >= 10 && phone.length <= 13) phoneNumber.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phone]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);
	const isMobileScreen = window.innerWidth <= 960;

	return (
		<>
			<Modal
				show={isOpen}
				size="md"
				onClose={() => {
					setOtp(['', '', '', '', '', '']);
					onClose();
				}}
				dismissible={true}
			>
				<Modal.Header className="border-b-0 dark:border-[#c7c9d91a] dark:bg-[#1F2A37] pb-0">
					<div>
						<div className="hukumu-update-phoneverification-title dark:text-white">Verify Your Phone Number</div>
					</div>
				</Modal.Header>
				<Modal.Body className="rounded-b-[6px] dark:bg-[#1F2A37] pt-[1px]">
					<div className="relative rounded-b-[6px]">
						{step === 0 && (
							<>
								<form
									onSubmit={e => {
										e.preventDefault();
										setTime(30);
										setStep(1);
										if (!phoneError)
											generateVerificationCodeFn.mutate({
												emailOrPhone: phone,
												userId: user?._id ?? '',
												isRecruiter: isRecruiterSide,
											});
									}}
								>
									<div className="mt-[26px]">You will receive a 6 digit code to verify next.</div>
									<div className="mt-[16px] text-base font-medium dark:text-white">Enter your number</div>
									<span className="text-[12px] text-red-500">{phoneError?.message}</span>
									<div className="mb-2 block">
										<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone " />
										<span className="text-red-600">*</span>
									</div>
									<div className="flex gap-[8px]">
										<input
											className="max-w-[50px] rounded-[8px] text-center text-base font-normal sm:pl-[8px] sm:text-[14px] md:max-w-[64px]"
											value={'+91'}
											disabled
										/>
										<TextInput
											className="modal-phone-number-input-box grow-2 rounded-[8px] text-base font-normal sm:text-[14px]"
											pattern={`^[0-9]{10}$`}
											defaultValue={phone}
											required={true}
											onChange={e => {
												debouncedChangeHandler(e);
											}}
										/>
									</div>
									<div className="flex justify-center">
										<Button
											color="purplePlaced"
											disabled={!phone || (phoneError && phoneError.type === 'failure')}
											type="submit"
											className="mt-[32px]"
										>
											Continue
										</Button>
									</div>
								</form>
							</>
						)}

						{step === 1 && (
							<>
								<form onSubmit={handleSubmit}>
									<div className="mt-[36px]">
										{'Check the code sent to '}
										<span className="dark:text-[#CCDDFF]">{phone?.trim()}</span>
										{' and enter it below.'}
									</div>

									<div
										onClick={hanldeEnterAnotherNumber}
										className="mt-[12px] cursor-pointer text-xs dark:text-white underline"
									>
										Change number
									</div>

									<div>
										<div className="mt-[16px] flex justify-start gap-2">
											<OtpInput
												isMobileScreen={isMobileScreen}
												handlePaste={handlePaste}
												handleKeyDown={handleKeyDown}
												handleChange={handleChange}
												otp={otp}
												inputRefs={inputRefs}
											/>
										</div>
									</div>

									<div className="mt-[8px] flex text-sm font-normal text-[#3F83F8]">
										<div
											style={{ cursor: time > 0 ? 'not-allowed' : 'pointer' }}
											onClick={() => {
												if (isResendEnabled) {
													setOtp(['', '', '', '', '', '']);
													generateVerificationCodeFn.mutate({
														emailOrPhone: phone,
														userId: user?._id ?? '',
														isRecruiter: isRecruiterSide,
													});
													setTime(30);
													start();
												}
											}}
										>
											Resend Code
										</div>

										<div className="ml-[4px]">
											{time > 0 && !isResendEnabled && (
												<div className="cursor-not-allowed">
													{'in '}
													<span className="dark:text-[#C7C9D9]">{time} sec</span>
												</div>
											)}
										</div>
									</div>

									<div className="hukumu-update-phoneverification-button-container mt-[32px] flex items-center justify-center gap-2">
										<div className="max-w-[332px]">
											<Button
												color="purplePlaced"
												disabled={isLoading || otp.join('').length < 6}
												type="submit"
												className="w-[100%] bg-[#7777E7]"
											>
												{isLoading ? <Spinner /> : 'VERIFY'}
											</Button>
										</div>
									</div>
								</form>
							</>
						)}
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
};

export default CompletePhoneVerificationModal;
